import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Globe } from "lucide-react";
import { inrShort } from "@/pages/Dashboard";

const fc = (cur, v) => `${cur} ${Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

export const FxExposureCard = ({ exposure, project }) => {
  const navigate = useNavigate();
  if (!exposure || !exposure.currencies?.length) return null;
  const go = (path, cur) => {
    const p = new URLSearchParams({ currency: cur });
    if (project?.length) p.set("project", project.join(","));
    navigate(`${path}?${p.toString()}`);
  };
  const net = exposure.net_inr;
  return (
    <Card className="border-sky-200 shadow-sm fade-in-up" data-testid="fx-exposure-card">
      <CardContent className="p-5">
        <div className="flex flex-wrap items-start justify-between gap-5">
          <div className="flex items-center gap-3.5">
            <div className="h-11 w-11 rounded-lg bg-sky-50 flex items-center justify-center shrink-0">
              <Globe className="text-sky-600" style={{ height: 22, width: 22 }} />
            </div>
            <div>
              <p className="text-xs font-semibold tracking-[0.05em] uppercase text-slate-500 mb-1">Foreign-Currency Exposure — Net (AR − AP)</p>
              <p className={`text-2xl sm:text-3xl font-black tracking-tighter ${net >= 0 ? "text-emerald-600" : "text-red-600"}`} data-testid="fx-exposure-net">{inrShort(net)}</p>
              <p className="text-[11px] text-slate-400 mt-1">
                {net >= 0 ? "Net FX receivable" : "Net FX payable"} · payables {inrShort(exposure.ap_inr)} vs receivables {inrShort(exposure.ar_inr)} (INR equivalent of open FX documents)
              </p>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="text-xs tabular-nums" data-testid="fx-exposure-table">
              <thead>
                <tr className="text-slate-500 uppercase tracking-wider text-[10px]">
                  <th className="text-left pr-4 pb-1">Currency</th>
                  <th className="text-right pr-4 pb-1">Payables (AP)</th>
                  <th className="text-right pr-4 pb-1">Receivables (AR)</th>
                  <th className="text-right pb-1">Net exposure</th>
                </tr>
              </thead>
              <tbody>
                {exposure.currencies.map((c) => (
                  <tr key={c.currency} className="border-t border-slate-100" data-testid={`fx-exposure-row-${c.currency}`}>
                    <td className="py-1.5 pr-4 font-bold text-slate-800">{c.currency}</td>
                    <td className="py-1.5 pr-4 text-right">
                      <button type="button" onClick={() => go("/ap-ageing", c.currency)} className="text-red-600 font-semibold hover:underline" data-testid={`fx-exposure-ap-${c.currency}`}>{fc(c.currency, c.ap_fc)}</button>
                      <div className="text-slate-400">≈ {inrShort(c.ap_inr)} · {c.ap_count} inv</div>
                    </td>
                    <td className="py-1.5 pr-4 text-right">
                      <button type="button" onClick={() => go("/ar-ageing", c.currency)} className="text-amber-600 font-semibold hover:underline" data-testid={`fx-exposure-ar-${c.currency}`}>{fc(c.currency, c.ar_fc)}</button>
                      <div className="text-slate-400">≈ {inrShort(c.ar_inr)} · {c.ar_count} inv</div>
                    </td>
                    <td className={`py-1.5 text-right font-bold ${c.net_inr >= 0 ? "text-emerald-600" : "text-red-600"}`} data-testid={`fx-exposure-net-${c.currency}`}>
                      {fc(c.currency, c.net_fc)}
                      <div className="font-normal text-slate-400">≈ {inrShort(c.net_inr)}</div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
